import { I18nProvider } from '@lingui/react';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
import { queryClient } from 'api';
import moment from 'moment';
import 'moment/dist/locale/fr';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import 'react-lazy-load-image-component/src/effects/black-and-white.css';
import 'react-lazy-load-image-component/src/effects/blur.css';
import 'react-lazy-load-image-component/src/effects/opacity.css';
import { QueryClientProvider } from '@tanstack/react-query';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import 'simplebar-react/dist/simplebar.min.css';
import { initApi } from 'initApi';
import { CollapseDrawerProvider } from 'contexts/CollapseDrawerContext';
import { app } from 'utils/appEnv';
import App from './App';
import { i18n, initLingui } from './locales/linguiI18n';

moment.locale('fr');

Sentry.init({
  dsn: 'https://038edc12b01746a883146e16866b7fc0@o412508.ingest.sentry.io/6459649',
  release: import.meta.env.RELEASE_ID,
  integrations: [
    new BrowserTracing(),
    new Sentry.Replay({
      maskAllText: false,
      maskAllInputs: false,
    }),
  ],
  tracesSampleRate: 0.3,
  environment: app.env,
  enabled: app.env !== 'development',
  ignoreErrors: ['globalThis is not defined'],
  replaysOnErrorSampleRate: 1.0,
  beforeSend: (event, hint) => {
    if (hint.originalException instanceof Error) {
      const exception = hint.originalException;

      if ('isAxiosError' in exception && exception.isAxiosError) {
        return null;
      }
    }

    return event;
  },
});

initApi();
await initLingui();

const container = document.getElementById('root');

if (!container) throw new Error('Failed to find the root element');

const root = createRoot(container);
root.render(
  <HelmetProvider>
    <I18nProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <CollapseDrawerProvider>
          <App />
        </CollapseDrawerProvider>
      </QueryClientProvider>
    </I18nProvider>
  </HelmetProvider>
);
